<script setup lang="ts">
import { ref, computed } from 'vue';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue';
import { ChevronDownIcon } from '@heroicons/vue/solid';
// Assuming types exist or need to be created
import type { Profile, NftCollection } from '~/types/generated'; // Adjust path/types as needed

const props = defineProps({
  slug: { type: String, required: true },
  collections: { type: Array as () => NftCollection[], required: true },
  profiles: { type: Array as () => Profile[], required: true },
  selectedCollectionIds: { type: Array as () => string[], required: true },
  selectedProfileIds: { type: Array as () => string[], required: true },
});

const emit = defineEmits(['update:selectedCollectionIds', 'update:selectedProfileIds']);

// --- Filter Logic ---
const toggleFilter = (type: 'collection' | 'profile', id: string) => {
  if (type === 'collection') {
    const currentIds = [...props.selectedCollectionIds];
    const index = currentIds.indexOf(id);
    if (index > -1) {
      currentIds.splice(index, 1);
    } else {
      currentIds.push(id);
    }
    emit('update:selectedCollectionIds', currentIds);
  } else if (type === 'profile') {
    const currentIds = [...props.selectedProfileIds];
    const index = currentIds.indexOf(id);
    if (index > -1) {
      currentIds.splice(index, 1);
    } else {
      currentIds.push(id);
    }
    emit('update:selectedProfileIds', currentIds);
  }
};

const isSelected = (type: 'collection' | 'profile', id: string): boolean => {
  if (type === 'collection') {
    return props.selectedCollectionIds.includes(id);
  } else if (type === 'profile') {
    return props.selectedProfileIds.includes(id);
  }
  return false;
};

const hasActiveCollectionFilter = computed(() => props.selectedCollectionIds.length > 0);
const hasActiveProfileFilter = computed(() => props.selectedProfileIds.length > 0);
</script>

<template>
  <aside>
    <form class="space-y-4" @submit.prevent="">
      <!-- Collections Filter -->
      <Disclosure v-if="collections?.length > 0" v-slot="{ open }" as="div" :default-open="true">
        <fieldset>
          <legend class="w-full">
            <DisclosureButton
              class="flex w-full items-center justify-between py-2 text-neutral-400 hover:text-neutral-500 dark:text-neutral-500"
            >
              <span
                class="text-start text-sm text-neutral-900 dark:text-neutral-50"
                :class="hasActiveCollectionFilter ? 'font-bold' : 'font-medium'"
              >
                Collections
                <!-- TODO: i18n -->
              </span>
              <span class="ml-6 flex h-7 items-center">
                <ChevronDownIcon :class="[open ? '-rotate-180' : 'rotate-0', 'h-5 w-5 transform']" aria-hidden="true" />
              </span>
            </DisclosureButton>
          </legend>
          <DisclosurePanel class="max-h-60 overflow-y-auto">
            <div class="space-y-2 pb-6 pt-2">
              <div v-for="collection in collections" :key="collection.id" class="flex items-center gap-2">
                <input
                  :id="`filter-collection-${collection.id}`"
                  :value="collection.id"
                  :checked="isSelected('collection', collection.id)"
                  type="checkbox"
                  class="h-4 w-4 rounded border-neutral-300 text-black focus:ring-black dark:border-neutral-600 dark:bg-neutral-800 dark:text-white"
                  @change="toggleFilter('collection', collection.id)"
                />
                <label
                  :for="`filter-collection-${collection.id}`"
                  class="flex-1 cursor-pointer text-sm text-neutral-600 dark:text-neutral-300"
                >
                  {{ collection.name }}
                </label>
              </div>
            </div>
          </DisclosurePanel>
        </fieldset>
      </Disclosure>

      <!-- Creators Filter -->
      <Disclosure v-if="profiles?.length > 0" v-slot="{ open }" as="div" :default-open="true">
        <fieldset>
          <legend class="w-full">
            <DisclosureButton
              class="flex w-full items-center justify-between py-2 text-neutral-400 hover:text-neutral-500 dark:text-neutral-500"
            >
              <span
                class="text-start text-sm text-neutral-900 dark:text-neutral-50"
                :class="hasActiveProfileFilter ? 'font-bold' : 'font-medium'"
              >
                Creators
                <!-- TODO: i18n -->
              </span>
              <span class="ml-6 flex h-7 items-center">
                <ChevronDownIcon :class="[open ? '-rotate-180' : 'rotate-0', 'h-5 w-5 transform']" aria-hidden="true" />
              </span>
            </DisclosureButton>
          </legend>
          <DisclosurePanel class="max-h-60 overflow-y-auto">
            <div class="space-y-2 px-1 pb-6 pt-2">
              <div v-for="profile in profiles" :key="profile.id" class="flex items-center gap-2">
                <input
                  :id="`filter-profile-${profile.id}`"
                  :value="profile.id"
                  :checked="isSelected('profile', profile.id)"
                  type="checkbox"
                  class="h-4 w-4 rounded border-neutral-300 text-black focus:ring-black dark:border-neutral-600 dark:bg-neutral-800 dark:text-white"
                  @change="toggleFilter('profile', profile.id)"
                />
                <label
                  :for="`filter-profile-${profile.id}`"
                  class="flex-1 cursor-pointer text-sm text-neutral-600 dark:text-neutral-300"
                >
                  <!-- TODO: Add avatar? -->
                  {{ profile.name || profile.encoded_id.slice(0, 20) + '...' }}
                </label>
              </div>
            </div>
          </DisclosurePanel>
        </fieldset>
      </Disclosure>
    </form>
  </aside>
</template>
